import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './ListTooltip.module.css';
import { Z_INDEX } from '../../styles/zIndex';

type Props = {
  /** ツールチップに表示する項目の配列 */
  items: string[];
  /** ツールチップのタイトル（オプション） */
  title?: string;
  children: ReactNode;
};

export function ListTooltip({ items, title, children }: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const padding = 8;

    // デフォルトは下に配置
    let top = triggerRect.bottom + padding;
    let left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;

    // 下に収まらない場合は上に配置
    if (top + tooltipRect.height > window.innerHeight - padding) {
      top = triggerRect.top - tooltipRect.height - padding;
    }

    // 上にも収まらない場合は画面内に収める
    if (top < padding) {
      top = padding;
    }

    // 左右の画面外調整
    if (left < padding) {
      left = padding;
    }
    if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }

    setPosition({ top, left });
  }, []);

  useEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [isVisible, updatePosition]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  }, [cancelHide]);

  const handleTriggerMouseEnter = () => {
    cancelHide();
    if (items.length > 0) {
      setIsVisible(true);
    }
  };

  const handleTriggerMouseLeave = () => {
    scheduleHide();
  };

  const handleTooltipMouseEnter = () => {
    cancelHide();
  };

  const handleTooltipMouseLeave = () => {
    scheduleHide();
  };

  // 項目がない場合はツールチップなしで子要素のみ表示
  if (items.length === 0) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={handleTriggerMouseLeave}
        style={{ display: 'inline-block' }}
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={styles.tooltip}
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              zIndex: Z_INDEX.tooltip,
            }}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
          >
            {title && <div className={styles.title}>{title}</div>}
            <ul className={styles.list}>
              {items.map((item, index) => (
                <li key={index} className={styles.item}>
                  {item}
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </>
  );
}
